/*
 * Copyright, AlpineReplay, Inc. 2021. All rights reserved.
 * Author: Brian Bal, Slobodan Djordjevic
 */
import { multisportify, startBenchmark } from "trux"
import { getUrlExtras } from "../../util/links"
import { apiFolder } from "../../util/apiFolder"
import { readClips } from "../../lib/PlaylistApi"
import { formatPlayerUnknown } from "../../trace/formatters"
import {
    getBaseballPositionSortIndex,
    isBaseballStatsAvailable,
} from "../../util/helpers"
import {
    filterEventsWithPlaylist,
    generatePlaylists,
    getPlayersPositions,
} from "../../util/playlistStore"
import localPlaylists from "./playlists.json"
import localTagPlaylists from "./tagPlaylists.json"
import baseballTagPlaylists from "./tagBaseballPlaylists.json"

let defaultValue = {
    loading: false,
    complete: false,
    error: null,
    value: null,
}

const loadGame = (teamName, gameId) => {
    return (dispatch, getState) => {
        let extras = getUrlExtras(window.location.hash)
        let gameState = getState().game
        let team = getState().team.value
        let gamesState = getState().games
        let user = getState().user.value
        let userComplete = getState().user.complete

        let gameIdInt = parseInt(gameId, 10)
        let games = getState().games.value
        let game = null
        if (games) {
            for (let g of games) {
                if (gameIdInt === g.game_id) {
                    game = JSON.parse(JSON.stringify(g))
                    break
                }
            }
        }

        if (!gameId && gameState.value) {
            // no game id reset
            dispatch({ type: "COMMON_GAME_RESET" })
            return
        } else if (!gameId) {
            return
        }

        if (game === null) {
            return { type: "NOOP" }
        } else if (gameState.loading) {
            return { type: "NOOP" }
        } else if (!gamesState.complete) {
            return { type: "NOOP" }
        }
        //else if (!team) {
        //    return { type: 'NOOP' }
        //}
        else if (team && team.name !== teamName) {
            dispatch({
                type: "COMMON_RESET",
            })
            return
        } else if (gameState.value && gameState.value.game_id !== gameIdInt) {
            dispatch({
                type: "COMMON_RESET",
            })
            return
        } else if (team && gameState.complete) {
            return { type: "NOOP" }
        } else if (userComplete === false) {
            return { type: "NOOP" }
        }

        startBenchmark("First Load")

        dispatch({
            type: "COMMON_SET",
            key: "game",
            value: {
                loading: true,
                complete: false,
                error: null,
                value: null,
            },
            defaultValue: { ...defaultValue },
        })

        let teamFolder = teamName
        if (extras["team-folder"]) {
            teamFolder = extras["team-folder"]
        }

        console.log("loadGame", "sport_type", game.sport_type)
        dispatch({
            type: "SET_SPORT_TYPE",
            sportType: game.sport_type,
        })

        let promises = [
            fetchGameTimeline(game, teamFolder, teamName, gameId),
            // fetchGameV2(game, teamFolder, teamName, gameId),
            loadMoments(game.game_id),
        ]

        if (game.sport_type === "baseball") {
            promises.push(
                fetchPlayersPositions(game, teamFolder, teamName, gameId)
            )
        }

        const timelineIndex = 0
        const gameV2Index = -1
        const momentsIndex = 1
        const playerPositionsIndex = game.sport_type === "baseball" ? 2 : -1

        Promise.all(promises)
            .then(async (results) => {
                // fixup game with all the info we need and legacy properties
                game.game_id_hash = teamName + "-" + gameId
                game.team_id = teamName
                game.processing = null
                game.goalies = []
                game.events = []

                if (timelineIndex >= 0) {
                    game.timeline = results[timelineIndex]
                        ? results[timelineIndex]
                        : { spotlight: [] }
                }
                if (gameV2Index >= 0) {
                    game.goalies =
                        results[gameV2Index] && results[gameV2Index].data
                            ? results[gameV2Index].data.goalies
                            : []
                }
                if (momentsIndex >= 0) {
                    game.events = results[momentsIndex]
                        ? results[momentsIndex]
                        : []
                }
                if (
                    game.sport_type === "baseball" &&
                    playerPositionsIndex >= 0
                ) {
                    if (results[playerPositionsIndex]) {
                        game.innings = results[playerPositionsIndex]
                    } else {
                        let inningsObject = {}
                        game.events.forEach((event) => {
                            event.tags.forEach((tag) => {
                                if (
                                    tag.name?.includes("inning") &&
                                    // eslint-disable-next-line no-prototype-builtins
                                    !inningsObject.hasOwnProperty(tag.name)
                                ) {
                                    inningsObject[tag.name] = {}
                                }
                            })
                        })
                        game.innings = inningsObject
                        dispatch({
                            type: "ALL_INNINGS",
                            allInnings: inningsObject,
                        })
                    }
                }

                game.playlistTemplates = await loadPlaylists(
                    game,
                    teamFolder,
                    game.events
                )
                return game
            })
            .then(async (game) => {
                // fixup events with goalies and tags and such
                let goalies = game.goalies ? game.goalies : []
                game.events = processEvents(game.events, goalies, game)
                if (user) {
                    game.events = await updateEventsWithProfilePlaylist(
                        user,
                        game.events
                    )
                }
                return game
            })
            .then((game) => {
                // fixup players
                for (let p of game.home_team.players) {
                    p.id = p.trace_number
                    p.unknown = formatPlayerUnknown(p)
                }
                game.team_folder = teamFolder
                return game
            })
            .then((game) => {
                // Create playlists
                let goalies = game.goalies ? game.goalies : []
                let templates = game.playlistTemplates

                let playlists = generatePlaylists(
                    templates,
                    [],
                    game.home_team.players,
                    game.events,
                    goalies,
                    game.video_render_type
                )

                if (game.sport_type === "baseball") {
                    let playersPositions = isBaseballStatsAvailable(
                        game.innings
                    )
                        ? []
                        : getPlayersPositions(game.innings)
                    for (let p of playlists) {
                        let pe = filterEventsWithPlaylist(p, game.events)
                        playlists.event_count = pe.length
                        if (p.id.includes("player")) {
                            p.positions = playersPositions.find(
                                (player) =>
                                    player.traceNumber ===
                                    parseInt(p.scope?.player?.trace_number)
                            )?.positions
                        }
                    }

                    dispatch({
                        type: "ALL_INNINGS",
                        allInnings: game.innings,
                    })
                }

                // set allCameras
                const allCameras = getAllCameras(game.events)
                dispatch({
                    type: "ALL_CAMERAS",
                    allCameras: allCameras,
                })

                game.playlists = playlists.filter((p) => {
                    if (p.id === "coach-notes") {
                        return p.event_count > 1
                    } else {
                        return p.event_count > 0 || p.show_when_empty
                    }
                })

                if (game.sport_type === "baseball") {
                    game.playlists.sort((a, b) => {
                        if (
                            a.id.includes("player") &&
                            b.id.includes("player")
                        ) {
                            let aIndex = a.positions
                                ? getBaseballPositionSortIndex(a.positions[0])
                                : 10
                            let bIndex = b.positions
                                ? getBaseballPositionSortIndex(b.positions[0])
                                : 10
                            return aIndex - bIndex
                        } else {
                            return 1
                        }
                    })
                }

                return game
            })
            .then((game) => {
                dispatch({
                    type: "COMMON_SET",
                    key: "game",
                    value: {
                        loading: false,
                        complete: true,
                        error: null,
                        value: game,
                    },
                    defaultValue: { ...defaultValue },
                })
                return game
            })
            .then((game) => {
                dispatch({
                    type: "COMMON_SET",
                    key: "game",
                    value: {
                        loading: false,
                        complete: true,
                        error: null,
                        value: game,
                    },
                    defaultValue: { ...defaultValue },
                })
            })
            .catch((err) => {
                console.error("loadGame - error", err)
                dispatch({
                    type: "COMMON_SET",
                    key: "game",
                    value: {
                        loading: false,
                        complete: true,
                        error: err,
                        value: null,
                    },
                    defaultValue: { ...defaultValue },
                })
            })
    }
}

function fullGameStartTime(event, allEvents) {
    let fullGameTime = 0
    let h1 = null

    if (event) {
        for (let ev of allEvents) {
            if (ev.meta.type === "FullGameVideo") {
                // eslint-disable-next-line eqeqeq
                if (ev.half == 1) {
                    // eslint-disable-next-line eqeqeq
                    if (h1 == null || ev.dynamic.start < h1.dynamic.start) {
                        h1 = ev
                    }
                    break
                }
            }
        }

        if (h1) {
            fullGameTime =
                Math.round((event.utc_time - h1.utc_time) / 1000) -
                event.dynamic.offset
        }
    }

    // console.log("event", event.dynamic.start, event.dynamic.offset, fullGameTime)

    return fullGameTime
}

function fetchGameV2(game, teamFolder, teamName, gameId) {
    return new Promise((resolve) => {
        let gameUrl = `${apiFolder(
            game
        )}/teams/${teamFolder}/games/${teamName}-${gameId}/game_v2.json`
        console.log("game_v2 url", gameUrl, game)

        fetch(gameUrl, { method: "GET", mode: "cors", cache: "no-cache" })
            .then((response) => response.json())
            .then((json) => {
                json = multisportify(json)
                resolve({ type: "v2", data: json })
            })
            .catch((err) => {
                console.warn(err)
                resolve({ type: "v2", data: null })
            })
    })
}

function fetchGameTimeline(game, teamFolder, teamName, gameId) {
    return new Promise((resolve) => {
        let url = `${apiFolder(
            game
        )}/teams/${teamFolder}/games/${teamName}-${gameId}/spotlight.json`
        return fetch(url)
            .then((response) => {
                return response.json()
            })
            .then((json) => {
                if (!json) {
                    json = { spotlight: [] }
                }
                json = multisportify(json)
                resolve(json)
            })
            .catch((err) => {
                console.error(err)
                resolve({ spotlight: [] })
            })
    })
}

function fetchPlayersPositions(game, teamFolder, teamName, gameId) {
    return new Promise((resolve) => {
        let url = `${apiFolder(
            game
        )}/teams/${teamFolder}/games/${teamName}-${gameId}/fielding_positions.json`
        return fetch(url)
            .then((response) => {
                return response.json()
            })
            .then((json) => {
                resolve(json)
            })
            .catch((err) => {
                console.error(err)
                resolve(null)
            })
    })
}

const loadPlaylists = (game, teamName, allEvents) => {
    return new Promise((resolve) => {
        let globalUrl = `${apiFolder(game)}/playlists.json`
        let teamUrl = `${apiFolder(game)}/teams/${teamName}/playlists.json`

        let tagVersion = getTagVersion(allEvents)
        game.tagVersion = tagVersion

        if (localPlaylists) {
            if (game.sport_type === "baseball") {
                resolve(baseballTagPlaylists)
            } else {
                if (tagVersion >= 3) {
                    resolve(localTagPlaylists)
                } else {
                    resolve(localPlaylists)
                }
            }
            return
        }

        fetchPlaylist(globalUrl, (globalPlaylist) => {
            fetchPlaylist(teamUrl, (teamPlaylist) => {
                let playlists = Object.assign({}, globalPlaylist, teamPlaylist)
                playlists = multisportify(playlists)
                resolve(playlists)
            })
        })
    })
}

const getTagVersion = (events, game) => {
    let v0 = 0
    let v3 = 0

    if (game && game.sport_type === "baseball") {
        return 3
    }

    for (let ev of events) {
        let human = false
        if (ev.tags) {
            // eslint-disable-next-line eqeqeq
            human = ev.tags.find((el) => el.name === "human created") != null
        }

        if (!human) {
            // eslint-disable-next-line eqeqeq
            if (ev.meta.tag_version == 3) {
                v3++
            } else {
                v0++
            }
        }
    }

    return v3 > v0 ? 3 : 0
}

function fetchPlaylist(url, callback) {
    fetch(url)
        .then((response) => response.json())
        .then((json) => {
            callback(json)
        })
        .catch((err) => {
            console.warn(err)
            callback({})
        })
}

function processEvents(actionEvents, goalies, game) {
    let allEvents = actionEvents.sort((a, b) => {
        return a.game_clip_id - b.game_clip_id
    })
    let tagVersion = getTagVersion(allEvents, game)
    let i = 0
    for (let event of allEvents) {
        event["event_number"] = i + 1
        if (event.sport_type !== game.sport_type) {
            event.sport_type = game.sport_type
        }
        // copy original title to orig_title as we can redefine 'title' value later
        event["orig_title"] = event["title"]
        event.meta.types = event.meta.type ? event.meta.type.split(" ") : []

        // add goalies to tracer numbers
        if (!event.meta.trace_numbers) {
            event.meta.trace_numbers = []
        }

        if (tagVersion < 3) {
            // change goalkeeper events to touch goalkeeper
            if (event.meta.type === "goalkeeper") {
                event.orig_type = "goalkeeper"
                event.meta.type = "touch chain goalkeeper"
            }
        }

        // Update full game video titles
        if (event.meta && event.meta.type === "FullGameVideo") {
            if (event.sport_type === "baseball") {
                event.title = "Raw Video"
            } else if (event.half === 1) {
                event.title = "First half raw video"
            } else if (event.half === 2) {
                event.title = "Second half raw video"
            } else {
                event.title = "Extra video 1"
            }
            //erase original title, it's a crap for full video
            event.orig_title = event.title
        }

        // add tags
        if (tagVersion < 3) {
            event.tags = generateTags(event)
        }

        // goalies
        event.meta.goalies = []
        if (
            event.meta &&
            event.meta.type.includes("key") &&
            event.meta.thirds &&
            event.meta.thirds.includes("defensive")
        ) {
            for (let g of goalies) {
                let gStart = g.utc_start_time * 1000
                let gEnd = g.utc_end_time * 1000
                if (gStart < event.utc_time && event.utc_time < gEnd) {
                    event.meta.trace_numbers.push(g.id)
                    event.meta.goalies.push(g.id)
                }
            }
        }
        if (event.meta && event.meta.type.includes("goalkeeper")) {
            for (let g of goalies) {
                let gStart = g.utc_start_time * 1000
                let gEnd = g.utc_end_time * 1000
                if (g.id && gStart < event.utc_time && event.utc_time < gEnd) {
                    event.meta.trace_numbers.push(g.id)
                    event.meta.goalies.push(g.id)
                }
            }
        }
        // remove duplicate players
        let numbers = []
        for (let tn of event.meta.trace_numbers) {
            let cnt = 0
            for (let tnb of numbers) {
                // eslint-disable-next-line eqeqeq
                if (tnb == tn) {
                    cnt++
                }
            }
            if (cnt === 0) {
                numbers.push(tn)
            }
        }
        event.meta.trace_numbers = numbers
        event.meta.all_trace_numbers = numbers

        // add players
        let players = []
        for (let tn of event.meta.trace_numbers) {
            for (let plyr of game.home_team.players) {
                // eslint-disable-next-line eqeqeq
                if (tn == plyr.trace_number) {
                    players.push({
                        ...plyr,
                        imageUrl: `https://lapi.traceup.com/tracebot-${process.env.REACT_APP_TRACE_ENV}/avatars?user=${plyr.user.user_id}`,
                    })
                    break
                }
            }
        }
        event.players = players
        event.full_game_time = fullGameStartTime(event, allEvents)
        i++
    }

    return allEvents
}
function generateTags(event) {
    let tags = []

    // third tag
    if (event.meta.thirds && event.meta.thirds.length > 1) {
        if (event.meta.thirds[0] === event.meta.thirds[1]) {
            tags.push({
                name: event.meta.thirds[0],
            })
        } else {
            tags.push({
                name: event.meta.thirds[0] + " to " + event.meta.thirds[1],
            })
        }
    }

    let homeBox = false
    let awayBox = false
    let packing = false
    let home = false
    let away = false

    if (event.meta.keywords) {
        homeBox = event.meta.keywords.includes("home box")
        awayBox = event.meta.keywords.includes("away box")
        packing = event.meta.keywords.includes("packing")
        away = event.meta.keywords.includes("away")
        home = event.meta.keywords.includes("home")
    }

    if (event.meta && event.meta.type) {
        if (event.meta.type.includes("touch chain")) {
            tags.push({ name: "touch chain" })
        } else if (event.meta.type.includes("touch")) {
            tags.push({ name: "touch" })
        }
        if (event.meta.type.includes("key")) {
            if (
                event.meta.thirds.length > 1 &&
                event.meta.thirds[0] === "defensive"
            ) {
                tags.push({ name: "away-goal" })
            } else {
                tags.push({ name: "goal" })
            }
        }
        if (event.meta.type.includes("goalkeeper") || homeBox || awayBox) {
            tags.push({ name: "box" })
        }
        if (away && packing) {
            tags.push({ name: "defensive breakdown" })
        } else if (home && packing) {
            tags.push({ name: "progression" })
        } else if (event.meta.type.includes("packing")) {
            tags.push({ name: "progression" })
        }
    }

    tags = tags.filter((t) => t && t.name && t.name.length > 0)

    return tags
}

function updateEventsWithProfilePlaylist(user, events) {
    return new Promise((resolve) => {
        let playlist = user.traceid_playlist
        if (playlist && Array.isArray(playlist.clips)) {
            for (let clip of playlist.clips) {
                for (let event of events) {
                    if (clip.game_clip_id === event.game_clip_id) {
                        event.user_playlist_key = playlist.hash_key
                        event.playlist_clip_id = clip.playlist_clip_id
                        event.collection_hash_key = clip.collection_hash
                    }
                }
            }
        }

        resolve(events)
    })
}

async function loadMoments(gameId) {
    let clips = await readClips(gameId)
    clips = clips.sort((a, b) => a.game_clip_id - b.game_clip_id)
    return clips
}

function getAllCameras(events) {
    let allCameras = new Map()

    events.forEach((event) => {
        // Check main event
        let camera = event.meta.camera
        if (camera) {
            allCameras.set(camera.name, camera)
        }

        // Check sources on event
        if (event.sources) {
            event.sources.forEach((source) => {
                let sourceCamera = source.meta.camera
                if (sourceCamera) {
                    allCameras.set(sourceCamera.name, sourceCamera)
                }
            })
        }
    })

    return [...allCameras.values()]
}

export default loadGame
